import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";

export default function AssessmentBanner() {
  return (
    <section
      id="assessment-banner"
      data-testid="assessment-banner"
      className="relative py-20 md:py-28 bg-[#1A2F24] text-[#FDFBF7] grain overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center relative">
        <div className="lg:col-span-7">
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#C5A059] mb-5">
            <span className="inline-block w-12 h-px bg-[#C5A059] mr-3 align-middle" />
            Free diagnostic tool
          </div>

          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
            className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            data-testid="assessment-banner-heading"
          >
            Is your finance function{" "}
            <em className="italic text-[#C5A059]">ready to scale?</em>
          </motion.h2>

          <p className="mt-6 text-[#D7D2C6] text-base md:text-lg leading-relaxed max-w-xl">
            Take our 3-minute Business Finance Health Assessment™ and receive a
            personalised report — with your Finance Health Score and a
            recommendation on whether an Outsourced Finance Department fits
            your business.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link
              to="/assessment"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#C5A059] text-[#111814] text-xs tracking-[0.18em] uppercase font-semibold hover:bg-[#D4B47A] transition-colors"
              data-testid="assessment-banner-cta-primary"
            >
              Take the Assessment <ArrowUpRight size={14} />
            </Link>
            <Link
              to="/assessment"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-[#C5A059] text-[#C5A059] text-xs tracking-[0.18em] uppercase font-semibold hover:bg-[#C5A059] hover:text-[#111814] transition-colors"
              data-testid="assessment-banner-cta-secondary"
            >
              What's inside
            </Link>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="relative bg-[#FDFBF7] text-[#111814] p-8 md:p-10">
            <Sparkles
              size={22}
              className="text-[#C5A059] absolute top-6 right-6"
              strokeWidth={1.5}
            />
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#68766E]">
              You'll receive
            </div>
            <div className="font-serif text-2xl md:text-3xl leading-tight mt-2">
              A branded PDF report
            </div>
            <ul className="mt-6 space-y-3 text-[15px] text-[#3A453F]">
              {[
                "Your Finance Health Score (0–100)",
                "Diagnostic breakdown across compliance, reporting & profitability",
                "Personalised recommendation on the right engagement model",
                "A 30-minute strategy call to review it together",
              ].map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <span className="mt-2 w-1.5 h-1.5 bg-[#C5A059] shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 pt-6 border-t border-[#E5E1D8] text-[11px] tracking-[0.18em] uppercase text-[#68766E]">
              3 minutes · Free · Confidential
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
